import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectors } from '@/features/connections';

import { DataTable } from '@/shared/ui/DataTable';
import { Indicator } from '@/shared/ui/Indicator';
import { Section, Sections } from '@/shared/ui/Layout';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { UnavailableState } from '@/shared/ui/States';

import type { ConnectorProfile } from '@/shared/api/generated/catalog';

/* What each connector family can do on this deployment — the honest answer to "why can't I add SQL
 * Server?".
 *
 * **Three separate capabilities, never collapsed into one "supported" flag.** Harvesting metadata,
 * executing rules and profiling columns are independent: Snowflake harvests and profiles and cannot
 * run a rule, and a screen that said "supported" would make a steward bind a rule that can never
 * execute. The API keeps them apart for exactly this reason and so does this table.
 *
 * **Read live from `/v1/catalog/connectors`, never a hardcoded list.** That endpoint derives its
 * answer from the three wiring registries in the running process, so it cannot claim a capability
 * the deployment lacks — and an unavailable family stays *visible* with its reason rather than being
 * filtered out, because "we do not support SQL Server" and "this build has no driver for it" are
 * different answers and only the second is true.
 */

export function ConnectorsScreen(): ReactElement {
  const { t } = useTranslation();
  const connectors = useConnectors();

  return (
    <Sections>
      <PageHeader title={t('connectors.title')} lede={t('connectors.lede')} />

      <Section title={t('connectors.matrix.title')} hint={t('connectors.matrix.hint')}>
        {connectors.isPending ? (
          <SkeletonRows rows={5} />
        ) : connectors.isError ? (
          <UnavailableState
            title={t('connectors.unreadable')}
            guidance={connectors.error.message}
          />
        ) : (
          <DataTable
            caption={t('connectors.matrix.caption')}
            columns={[
              { key: 'family', label: t('connectors.column.family') },
              { key: 'harvest', label: t('connectors.column.harvest') },
              { key: 'rules', label: t('connectors.column.rules') },
              { key: 'profile', label: t('connectors.column.profile') },
              { key: 'fields', label: t('connectors.column.fields') },
            ]}
          >
            {connectors.data.items.map((profile) => (
              <tr key={profile.connector_type}>
                <td>
                  <span className="lq-mono">{profile.connector_type}</span>
                </td>
                <td>
                  <Capability able={profile.harvestable} />
                </td>
                <td>
                  <Capability able={profile.executes_rules} />
                </td>
                <td>
                  <Capability able={profile.profiles_columns} />
                </td>
                <td>{describeFields(profile, t)}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </Section>
    </Sections>
  );
}

/* One capability, as a mark rather than a colour alone.
 *
 * `Indicator`'s hollow dot for the absent case, matching every other absence in this kit: a family
 * that cannot do something is not *failing* at it, so a red mark would be wrong — it is a capability
 * this build does not have, which is the same category as "we could not measure this". */
function Capability({ able }: { readonly able: boolean }): ReactElement {
  const { t } = useTranslation();
  return (
    <Indicator
      tone={able ? 'ok' : 'unknown'}
      label={able ? t('connectors.able') : t('connectors.unable')}
    />
  );
}

/* The credential fields a family needs, or the reason there are none.
 *
 * An empty `required_fields` is not an easy connector — it is a family with **no adapter on this
 * deployment**, and rendering it as a blank cell would read as "needs nothing". The two are opposite
 * facts. */
function describeFields(
  profile: ConnectorProfile,
  t: (key: string) => string,
): ReactElement {
  if (!profile.harvestable && profile.required_fields.length === 0) {
    return <span className="lq-muted">{t('connectors.noAdapter')}</span>;
  }
  return <span className="lq-mono">{profile.required_fields.join(' · ')}</span>;
}
